class QuizSection extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Get DOM elements
    this.progressFill = this.querySelector('.quiz-progress-fill');
    this.steps = this.querySelectorAll('.quiz-step');
    this.nextBtn = this.querySelector('#quiz-next-btn');
    this.backBtn = this.querySelector('#quiz-back-btn');
    this.nextText = this.querySelector('#quiz-next-text');
    this.resultsSection = this.querySelector('#quiz-results');
    this.stepsWrapper = this.querySelector('.quiz-steps-wrapper');
    this.quizContainer = this.querySelector('.quiz-container-js');

    // Form elements for final step
    this.nameInput = this.querySelector('#quiz-name');
    this.emailInput = this.querySelector('#quiz-email');
    this.consentCheckbox = this.querySelector('#quiz-consent');

    // Quiz state
    this.currentStep = 1;
    this.totalSteps = this.steps.length;
    this.answers = {};
    this.questionCounter = 1; // Track logical question number
    this.visitedSteps = [1]; // Track the sequence of visited steps

    // Initialize
    this.setupEventListeners();
    this.updateQuestionNumber(); // Set initial question number
    this.updateUI();
    this.updateProgressBar();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset') === 'true') {
      this.resetQuiz();
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('reset');
      window.history.replaceState({}, '', newUrl);
    } else {
      this.loadFromCartAttributes();
    }

    //console.log(`Quiz initialized with ${this.totalSteps} steps`);

    this.addEventListener('quiz-data-saved', (e) => {
      //console.log('quiz-data-saved', e.detail);
      document.querySelector('quiz-result-products').init();
    });
  }

  setupEventListeners() {
    // Next button
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.handleNext());
    }

    // Back button
    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.handleBack());
    }

    // Answer change listeners
    this.steps.forEach((step, index) => {
      const inputs = step.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.enforceExclusiveAnswer(input, step);
          this.handleAnswerChange(index + 1);
        });
      });
    });

    // Final step form listeners
    if (this.nameInput) {
      this.nameInput.addEventListener('input', () => this.handleFinalStepChange());
    }
    if (this.emailInput) {
      this.emailInput.addEventListener('input', () => this.handleFinalStepChange());
    }
    if (this.consentCheckbox) {
      this.consentCheckbox.addEventListener('change', () => this.handleFinalStepChange());
    }

    // Reset button
    const resetBtn = this.querySelector('.quiz-btn-reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.resetQuiz());
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => this.handleKeydown(e));
  }

  handleNext() {
    if (this.currentStep < this.totalSteps) {
      const nextStep = this.getNextStep();
      // Add the next step to visited steps
      this.visitedSteps.push(nextStep);
      // Increment question counter
      this.questionCounter++;
      this.goToStep(nextStep);
    } else {
      this.submitQuiz();
    }
  }

  getNextStep() {
    // Check if current step has any selected answers with skip-next-question="true"
    const currentStepElement = this.steps[this.currentStep - 1];
    if (currentStepElement) {
      const checkedInputs = currentStepElement.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');

      for (const input of checkedInputs) {
        if (input.dataset.skipNextQuestion === 'true') {
          // Skip one step ahead if we're not at the last step
          const skipToStep = this.currentStep + 2;
          return skipToStep <= this.totalSteps ? skipToStep : this.currentStep + 1;
        }
      }
    }

    // Default: go to next step
    return this.currentStep + 1;
  }

  handleBack() {
    if (this.visitedSteps.length > 1) {
      // Remove the current step from visited steps
      this.visitedSteps.pop();
      // Get the previous visited step
      const previousStep = this.visitedSteps[this.visitedSteps.length - 1];
      // Decrement question counter
      this.questionCounter--;
      this.goToStep(previousStep);
    }
  }

  handleKeydown(e) {
    // Allow Enter to proceed if Next button is enabled
    if (e.key === 'Enter' && !this.nextBtn.disabled) {
      e.preventDefault();
      this.handleNext();
    }

    // Allow Escape to go back
    if (e.key === 'Escape' && !this.backBtn.disabled) {
      e.preventDefault();
      this.handleBack();
    }
  }

  goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > this.totalSteps) return;

    const currentStepElement = this.steps[this.currentStep - 1];
    const newStepElement = this.steps[stepNumber - 1];

    // Update current step immediately
    this.currentStep = stepNumber;

    // Start exit animation for current step
    if (currentStepElement) {
      currentStepElement.setAttribute('data-active', 'false');
      currentStepElement.classList.add('quiz-step-exit');
    }

    // Start enter animation for new step (overlapping with exit)
    if (newStepElement) {
      // Show new step immediately but with opacity 0 (handled by CSS)
      newStepElement.style.display = 'block';
      newStepElement.setAttribute('data-active', 'true');

      // Small delay to ensure display is applied before animation
      requestAnimationFrame(() => {
        newStepElement.classList.add('quiz-step-enter');
      });
    }

    // Clean up animations after they complete
    setTimeout(() => {
      if (currentStepElement) {
        currentStepElement.style.display = 'none';
        currentStepElement.classList.remove('quiz-step-exit');
      }
      if (newStepElement) {
        newStepElement.classList.remove('quiz-step-enter');
      }
    }, 400);

    // Update question number and UI
    this.updateQuestionNumber();
    this.updateUI();
    this.updateProgressBar();

    // Scroll to top of quiz
    if (!this.quizContainer.classList.contains('quiz-hidden')) {
      this.quizContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // An answer flagged "checked only" (e.g. "No concerns") cannot be combined with other answers:
  // ticking it clears the others, and ticking any other answer clears it.
  enforceExclusiveAnswer(changedInput, step) {
    if (changedInput.type !== 'checkbox' || !changedInput.checked) return;

    const isExclusive = changedInput.dataset.checkedOnly === 'true';
    const group = step.querySelectorAll(`input[type="checkbox"][name="${changedInput.name}"]`);

    group.forEach((input) => {
      if (input === changedInput) return;
      if (isExclusive || input.dataset.checkedOnly === 'true') {
        input.checked = false;
      }
    });
  }

  handleAnswerChange(stepNumber) {
    // Store answers
    this.storeAnswers(stepNumber);

    // Update Next button state if this is the current step
    if (stepNumber === this.currentStep) {
      this.updateNextButton();
    }
  }

  storeAnswers(stepNumber) {
    const step = this.steps[stepNumber - 1];
    if (!step) return;

    const inputs = step.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    const checkedInputs = Array.from(step.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked'));
    const answers = [];

    if (checkedInputs.length > 0) {
      const values = checkedInputs.map((input) => input.value);
      const propsValues = checkedInputs.map((input) => input.dataset.value);
      const metaobjectHandles = checkedInputs.map((input) => input.dataset.answerMetaobjectHandle);
      const input = checkedInputs[0];
      const questionKey = input.dataset.key;

      answers.push({
        questionKey: questionKey,
        propValues: propsValues,
        metaobjectHandles: metaobjectHandles,
      });
    }

    this.answers[stepNumber] = answers;
    //console.log(`Answers for step ${stepNumber}:`, answers);
  }

  getQuestionText(step) {
    const questionTitle = step.querySelector('.quiz-question-title');
    return questionTitle ? questionTitle.textContent.trim() : `Question ${this.currentStep}`;
  }

  updateNextButton() {
    let canProceed = false;

    // Check if this is the final step (lead capture)
    if (this.currentStep === this.totalSteps && this.querySelector('.quiz-step-final')) {
      canProceed = this.validateFinalStep();
    } else {
      // Regular question step
      const currentStepAnswers = this.answers[this.currentStep] || [];
      canProceed = currentStepAnswers.length > 0;
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = !canProceed;

      // Update button text for last step
      if (this.nextText) {
        this.nextText.textContent = this.currentStep === this.totalSteps ? 'Finish Up!' : 'Next';
      }
    }
  }

  handleFinalStepChange() {
    if (this.currentStep === this.totalSteps) {
      this.updateNextButton();
    }
  }

  validateFinalStep() {
    if (!this.nameInput || !this.emailInput) return false;

    const nameValid = this.nameInput.value.trim().length > 0;
    const emailValid = this.emailInput.value.trim().length > 0 && this.isValidEmail(this.emailInput.value);

    // Consent is considered given if there's no checkbox (implied consent with SVG design)
    const consentValid = !this.consentCheckbox || this.consentCheckbox.checked;

    return nameValid && emailValid && consentValid;
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  updateUI() {
    // Update back button
    if (this.backBtn) {
      this.backBtn.disabled = this.visitedSteps.length <= 1;
    }

    // Update next button
    this.updateNextButton();

    // Update step indicators or any other UI elements
    this.updateStepIndicators();
  }

  updateStepIndicators() {
    // If you have step indicators in the UI, update them here
    // This is optional and can be customized based on your design needs
  }

  updateProgressBar() {
    if (!this.progressFill) return;

    // Calculate progress percentage based on current step (not completed steps)
    // This ensures first step shows progress, not 0%
    const progressPercentage = ((this.currentStep - 1) / this.totalSteps) * 100;

    this.progressFill.style.width = `${Math.max(progressPercentage, 10)}%`;
  }

  updateQuestionNumber() {
    const currentStepElement = this.steps[this.currentStep - 1];
    if (!currentStepElement) return;

    const questionNumberElement = currentStepElement.querySelector('.quiz-question-number');
    if (!questionNumberElement) return;

    questionNumberElement.textContent = `${this.questionCounter}.`;
  }

  submitQuiz() {
    // Collect all answers
    const allAnswers = this.getAllAnswers();

    // Collect lead data from final step
    const leadData = this.getLeadData();

    // Combine quiz data
    const quizData = {
      answers: allAnswers,
      leadData: leadData,
      timestamp: new Date().toISOString(),
    };

    // Log answers (you can replace this with actual submission logic)
    //console.log('Quiz submitted with data:', quizData);

    // Update progress bar to 100%
    if (this.progressFill) {
      this.progressFill.style.width = '100%';
    }

    // Send data to Shopify cart attributes first, then show results
    this.sendAnswersToServer(quizData);
  }

  getLeadData() {
    if (!this.nameInput || !this.emailInput) {
      return null;
    }

    return {
      name: this.nameInput.value.trim(),
      email: this.emailInput.value.trim(),
      consent: this.consentCheckbox ? this.consentCheckbox.checked : true, // Default true if no checkbox
    };
  }

  getAllAnswers() {
    const allAnswers = [];

    Object.keys(this.answers).forEach((stepNumber) => {
      const stepAnswers = this.answers[stepNumber];
      stepAnswers.forEach((answer) => {
        allAnswers.push({
          step: parseInt(stepNumber),
          ...answer,
        });
      });
    });
    //console.log('allAnswers', allAnswers);
    return allAnswers;
  }

  showResults(quizData, scroll = true) {
    const resultsPageUrl = this.getResultsPageUrl();
    console.log('Results page URL:', resultsPageUrl);

    if (resultsPageUrl) {
      // Redirect to the separate results page
      console.log('Redirecting to results page:', resultsPageUrl);
      window.location.href = resultsPageUrl;
      return;
    }

    this.updateResultsHeaderName(quizData?.leadData?.name);
    this.setResultsView();

    // Scroll to results
    if (scroll) {
      this.resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  getResultsPageUrl() {
    const resultsUrl = this.getAttribute('data-results-page-url');
    return resultsUrl || null;
  }

  updateResultsHeaderName(name) {
    // Get the user's name from the input field
    const userName = name || (this.nameInput ? this.nameInput.value.trim() : '');

    // Find the name span in the results header
    const userNameSpan = this.querySelector('#quiz-user-name');

    if (userNameSpan && userName) {
      // Update the name in the header
      userNameSpan.textContent = userName;
    } else if (userNameSpan) {
      // Fallback to a friendly greeting if no name provided
      userNameSpan.textContent = 'there';
    }
  }

  sendAnswersToServer(quizData) {
    // Prepare cart attributes from quiz data
    const cartAttributes = this.prepareCartAttributes(quizData);

    // Save to Shopify cart attributes
    this.saveToCartAttributes(cartAttributes)
      .then((response) => {
        //console.log('Quiz data saved to cart attributes successfully:', response);

        // Send data to Klaviyo after successful cart save
        this.sendToKlaviyo(quizData);

        // Show results after cart attributes are saved
        this.showResults(quizData);

        this.dispatchEvent(new CustomEvent('quiz-data-saved', { detail: quizData }));
      })
      .catch((error) => {
        console.error('Error saving quiz data to cart attributes:', error);
        this.showResults(quizData);
      });
  }

  sendToKlaviyo(quizData) {
    console.log('sendToKlaviyo', quizData);
    // Get Klaviyo configuration from theme settings or window
    let klaviyoCompanyId = window.FoxTheme?.settings?.klaviyoCompanyId || window.klaviyoCompanyId || this.getAttribute('data-klaviyo-company-id');

    if (!klaviyoCompanyId) {
      console.warn('Klaviyo company ID not configured. Profile creation skipped.');
      return;
    }

    // Prepare Klaviyo profile data
    const profileData = this.prepareKlaviyoProfileData(quizData);

    // Send to Klaviyo
    this.createKlaviyoProfile(klaviyoCompanyId, profileData)
      .then((response) => {
        //console.log('Klaviyo profile created/updated successfully:', response);
      })
      .catch((error) => {
        console.error('Error creating/updating Klaviyo profile:', error);
      });
  }

  prepareKlaviyoProfileData(quizData) {
    const properties = {};

    // Add quiz completion metadata
    properties['quiz_completed'] = 'true';
    properties['quiz_completed_at'] = quizData.timestamp;

    // Add each quiz answer as key-value pairs using questionKey and propValues
    quizData.answers.forEach((answer) => {
      const questionKey = answer.questionKey;

      // Add property values as key-value pairs (for segmentation and personalization)
      if (answer.propValues && answer.propValues.length > 0) {
        properties[questionKey] = answer.propValues.join(', ');
      }
    });

    // Collect all metaobject handles for product recommendations
    const allMetaobjectHandles = [];
    quizData.answers.forEach((answer) => {
      if (answer.metaobjectHandles && answer.metaobjectHandles.length > 0) {
        allMetaobjectHandles.push(...answer.metaobjectHandles);
      }
    });

    if (allMetaobjectHandles.length > 0) {
      properties['quiz_answers_handles'] = allMetaobjectHandles.join(',');
    }

    const firstName = quizData.leadData?.name?.split(' ')[0] || '';
    const lastName = quizData.leadData?.name?.split(' ').slice(1).join(' ') || '';
    // Prepare the profile data structure for Klaviyo API
    const profileData = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                properties,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                      consented_at: new Date().toISOString(),
                    },
                  },
                },
                email: quizData.leadData?.email,
                first_name: firstName,
                last_name: lastName,
              },
            },
          },
          custom_source: 'Quiz',
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.getAttribute('data-klaviyo-list-id'),
            },
          },
        },
      },
    };

    // Add lead data (email and name)
    /* if (quizData.leadData) {
      // Add consent information
      if (quizData.leadData.consent !== undefined) {
        profileData.data.attributes.properties['marketing_consent'] = quizData.leadData.consent;
      }
    } */

    return profileData;
  }

  async createKlaviyoProfile(companyId, profileData) {
    // if in future we would only create/update profiles NOT subscribe,
    // then we can later use this endpoint instead https://developers.klaviyo.com/en/reference/create_client_profile

    // https://developers.klaviyo.com/en/reference/create_client_subscription
    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${companyId}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          Revision: '2025-07-15',
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Klaviyo API error (${response.status}): ${errorText}`);
      }

      return response.ok;
    } catch (error) {
      console.error('Klaviyo profile creation failed:', error);
      throw error;
    }
  }

  prepareCartAttributes(quizData) {
    const attributes = {};
    const allMetaobjectHandles = [];

    // Add lead data (name and email)
    if (quizData.leadData) {
      if (quizData.leadData.name) {
        attributes['quiz_user_name'] = quizData.leadData.name;
      }
      if (quizData.leadData.email) {
        attributes['quiz_user_email'] = quizData.leadData.email;
      }
      attributes['quiz_consent'] = quizData.leadData.consent ? 'true' : 'false';
    }

    // Add quiz answers - only questionKey and propValues
    quizData.answers.forEach((answer) => {
      const questionKey = answer.questionKey;

      // Save property values if available (for filtering/recommendations)
      if (answer.propValues && answer.propValues.length > 0) {
        attributes[`quiz_${questionKey}`] = answer.propValues.join(', ');
      }

      // Collect metaobject handles from this answer
      if (answer.metaobjectHandles && answer.metaobjectHandles.length > 0) {
        allMetaobjectHandles.push(...answer.metaobjectHandles);
      }
    });

    // Save all metaobject handles as one comma-separated attribute (for product recommendations)
    if (allMetaobjectHandles.length > 0) {
      attributes['quiz_answers_meta_handles'] = allMetaobjectHandles.join(',');
    }

    // Add metadata
    attributes['quiz_completed'] = true;
    attributes['quiz_completed_at'] = quizData.timestamp;

    //console.log('Prepared cart attributes:', attributes);
    return attributes;
  }

  async saveToCartAttributes(attributes) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: attributes,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Cart attributes update failed:', error);
      throw error;
    }
  }

  // Cart Attributes Methods
  async loadFromCartAttributes() {
    try {
      const cartData = await this.fetchCartAttrs();
      if (cartData) {
        const quizData = this.parseQuizAttributes(cartData);
        //console.log('quizData', quizData);
        if (quizData) {
          //console.log('Found existing quiz data in cart:', quizData);

          // Check if quiz is completeview
          if (this.isQuizComplete(quizData)) {
            // Show results directly
            //console.log('Quiz is complete, showing results');
            this.showResults(quizData, false);
          } else {
            // Populate partial data
            //console.log('Populating partial quiz data');
            this.populateQuizFromData(quizData);
          }
        }
      }
    } catch (error) {
      console.warn('Could not load cart attributes:', error);
      // Continue with fresh quiz if cart loading fails
    }
  }

  async fetchCartAttrs(fromFetch = false) {
    // instead of fetch request let's parse the data from the script tag
    const scriptTag = document.querySelector('script[type="application/json"][data-cart-attributes-json]');
    if (scriptTag && !fromFetch) {
      const data = JSON.parse(scriptTag.textContent);
      return data;
    }

    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      return cartData.attributes;
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
      throw error;
    }
  }

  parseQuizAttributes(attributes) {
    const quizData = {
      leadData: {},
      answers: [],
      timestamp: null,
      metaobjectHandles: [],
    };

    let hasQuizData = false;

    // Parse lead data
    if (attributes.quiz_user_name) {
      quizData.leadData.name = attributes.quiz_user_name;
      hasQuizData = true;
    }
    if (attributes.quiz_user_email) {
      quizData.leadData.email = attributes.quiz_user_email;
      hasQuizData = true;
    }
    if (attributes.quiz_consent) {
      quizData.leadData.consent = attributes.quiz_consent === 'true';
      hasQuizData = true;
    }

    // Parse quiz answers (simplified format: quiz_{questionKey} = propValues)
    const questionKeys = new Set();
    Object.keys(attributes).forEach((key) => {
      if (key.startsWith('quiz_') && !key.includes('user_') && !key.includes('consent') && !key.includes('completed') && !key.includes('meta_handles')) {
        const questionKey = key.replace('quiz_', '');
        questionKeys.add(questionKey);
      }
    });
    console.log('questionKeys', questionKeys);

    questionKeys.forEach((questionKey) => {
      const propValues = attributes[`quiz_${questionKey}`];

      if (propValues) {
        quizData.answers.push({
          questionKey,
          propValues: propValues.split(', '),
          metaobjectHandles: [], // Will be populated if needed
        });
        hasQuizData = true;
      }
    });

    // Parse metadata
    if (attributes.quiz_completed_at) {
      quizData.timestamp = attributes.quiz_completed_at;
      hasQuizData = true;
    }
    if (attributes.quiz_completed === 'true') {
      hasQuizData = true;
    }

    // Parse metaobject handles
    if (attributes.quiz_answers_meta_handles) {
      quizData.metaobjectHandles = attributes.quiz_answers_meta_handles.split(',');
      hasQuizData = true;
    }

    return hasQuizData ? quizData : null;
  }

  isQuizComplete(quizData) {
    // Check if we have lead data and quiz is marked as completed
    const hasLeadData = quizData.leadData.name && quizData.leadData.email;
    const hasAnswers = quizData.answers.length > 0;
    const hasMetaobjectHandles = quizData.metaobjectHandles && quizData.metaobjectHandles.length > 0;

    return hasLeadData && hasAnswers && hasMetaobjectHandles;
  }

  populateQuizFromData(quizData) {
    // Populate lead form if available
    if (quizData.leadData.name && this.nameInput) {
      this.nameInput.value = quizData.leadData.name;
    }
    if (quizData.leadData.email && this.emailInput) {
      this.emailInput.value = quizData.leadData.email;
    }
    if (quizData.leadData.consent !== undefined && this.consentCheckbox) {
      this.consentCheckbox.checked = quizData.leadData.consent;
    }

    // Populate quiz answers
    quizData.answers.forEach((answerData) => {
      this.populateStepAnswer(answerData);
    });

    // Update stored answers
    this.populateStoredAnswers(quizData);

    // Update UI
    this.updateUI();
    this.updateProgressBar();
  }

  populateStepAnswer(answerData) {
    // Find the step that contains this questionKey
    this.steps.forEach((step, stepIndex) => {
      const inputs = step.querySelectorAll('input[type="radio"], input[type="checkbox"]');

      inputs.forEach((input) => {
        // Match by questionKey (data-key attribute)
        if (input.dataset.key === answerData.questionKey && answerData.propValues.includes(input.dataset.value)) {
          input.checked = true;
        }
      });
    });
  }

  populateStoredAnswers(quizData) {
    // Convert the loaded answers back to the internal format
    quizData.answers.forEach((answerData) => {
      // Find which step this belongs to by matching questionKey
      this.steps.forEach((step, stepIndex) => {
        const inputs = step.querySelectorAll('input[type="radio"], input[type="checkbox"]');
        const firstInput = inputs[0];

        if (firstInput && firstInput.dataset.key === answerData.questionKey) {
          const stepNumber = stepIndex + 1;
          this.answers[stepNumber] = [
            {
              questionKey: answerData.questionKey,
              propValues: answerData.propValues,
              metaobjectHandles: answerData.metaobjectHandles || [],
            },
          ];
        }
      });
    });
  }

  setResultsView() {
    // Hide the steps wrapper
    if (this.quizContainer) {
      this.quizContainer.classList.add('quiz-hidden');
    }

    // Show results section
    if (this.resultsSection) {
      this.resultsSection.classList.remove('quiz-hidden');
    }

    // Update progress bar to 100%
    if (this.progressFill) {
      this.progressFill.style.width = '100%';
    }
  }

  // Utility methods
  resetQuiz() {
    this.currentStep = 1;
    this.answers = {};
    this.questionCounter = 1;
    this.visitedSteps = [1];

    // Clear form inputs
    if (this.nameInput) this.nameInput.value = '';
    if (this.emailInput) this.emailInput.value = '';
    if (this.consentCheckbox) this.consentCheckbox.checked = false;

    // Reset UI
    this.steps.forEach((step, index) => {
      step.style.display = index === 0 ? 'block' : 'none';
      step.setAttribute('data-active', index === 0 ? 'true' : 'false');

      // Clear all inputs
      const inputs = step.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      inputs.forEach((input) => {
        input.checked = false;
      });
    });

    // Reset results section
    if (this.resultsSection) {
      this.resultsSection.classList.add('quiz-hidden');
    }

    // Show quiz container
    if (this.quizContainer) {
      this.quizContainer.classList.remove('quiz-hidden');
    }

    // Show navigation
    const navigation = this.querySelector('.quiz-navigation');
    if (navigation) {
      navigation.classList.remove('quiz-hidden');
    }

    // Clear cart attributes
    this.clearQuizFromCartAttributes();

    this.updateUI();
    this.updateProgressBar();

    // Scroll to top of quiz
    this.quizContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async clearQuizFromCartAttributes() {
    try {
      const attributes = await this.fetchCartAttrs(true);
      if (attributes) {
        const clearedAttributes = {};

        // Find all quiz-related attributes and set them to empty string to remove them
        Object.keys(attributes).forEach((key) => {
          if (key.startsWith('quiz_')) {
            clearedAttributes[key] = '';
          }
        });

        if (Object.keys(clearedAttributes).length > 0) {
          await this.saveToCartAttributes(clearedAttributes);
          //console.log('Quiz attributes cleared from cart');
        }
      }
    } catch (error) {
      console.warn('Could not clear quiz attributes from cart:', error);
    }
  }

  getCurrentStepData() {
    return {
      currentStep: this.currentStep,
      totalSteps: this.totalSteps,
      answers: this.answers[this.currentStep] || [],
      allAnswers: this.getAllAnswers(),
    };
  }
}

window.customElements.define('quiz-section', QuizSection);
